// runToolLoop — client-side tool execution loop.
//
// Pattern:
//   1. POST /infer với messages + tools schema → SSE stream
//   2. Stream emit `delta` (text) → callback onDelta cập nhật UI dần
//   3. Stream emit `tool_calls` → cho mỗi call:
//      - Match tool name local
//      - Nếu tool.needsApproval → await onApprovalRequired(call) (boolean)
//      - Nếu approve / không cần → tool.execute(args), capture result
//      - Push {role:"tool", tool_call_id, content: JSON.stringify(result)} vào messages
//   4. Loop POST /infer lại với messages cập nhật
//   5. Max iterations để tránh runaway (default 5)
//   6. Khi gặp `finish_reason=stop` hoặc error → break, return assistant message cuối

import type {
  AgentClient,
} from "./client";
import {
  GatewayError,
  type ChatMessage,
  type FinishReason,
  type InferConfig,
  type Tool,
  type ToolCall,
} from "./types";

/// Fallback parser: some models (vd `@cf/zai-org/glm-4.7-flash`) trên Workers AI
/// emit tool calls dưới dạng JSON inline trong assistant content thay vì
/// structured `tool_calls` events. Scan content tìm JSON objects shape
/// `{"name": "...", "arguments": {...}}` và synthesize ToolCall[].
///
/// Strategy: regex tìm `"name":"<id>"` rồi balance-scan `{...}` để lấy full object.
/// Hỗ trợ:
///   - Code fences ```json ... ``` (strip trước scan)
///   - Nested objects trong arguments (balance-scan)
///   - Nhiều tool calls liên tiếp
///   - Args là object hoặc string-encoded JSON
export function extractTextToolCalls(text: string): ToolCall[] {
  if (!text) return [];
  // Strip code fences ```json ... ``` để regex bắt được dễ hơn.
  const stripped = text.replace(/```(?:json)?\s*/gi, "").replace(/```/g, "");

  const results: ToolCall[] = [];
  // Find each `"name": "<tool_name>"` occurrence, scan back để tìm `{` mở
  // tương ứng, rồi forward balance-scan `}`.
  const nameRe = /"name"\s*:\s*"([a-z_][a-z0-9_]*)"/gi;
  let m: RegExpExecArray | null;

  while ((m = nameRe.exec(stripped)) !== null) {
    // Scan back tìm `{` mở object chứa "name"
    let openIdx = -1;
    let depth = 1;
    for (let i = m.index - 1; i >= 0; i--) {
      const c = stripped[i];
      if (c === "}") depth++;
      else if (c === "{") {
        depth--;
        if (depth === 0) {
          openIdx = i;
          break;
        }
      }
    }
    if (openIdx === -1) continue;

    // Forward balance-scan đến `}` đóng tương ứng
    let endIdx = -1;
    let d = 0;
    let inString = false;
    let escape = false;
    for (let i = openIdx; i < stripped.length; i++) {
      const c = stripped[i];
      if (escape) {
        escape = false;
        continue;
      }
      if (c === "\\") {
        escape = true;
        continue;
      }
      if (c === '"') {
        inString = !inString;
        continue;
      }
      if (inString) continue;
      if (c === "{") d++;
      else if (c === "}") {
        d--;
        if (d === 0) {
          endIdx = i + 1;
          break;
        }
      }
    }
    if (endIdx === -1) continue;

    const blob = stripped.slice(openIdx, endIdx);
    try {
      const parsed = JSON.parse(blob) as { name?: unknown; arguments?: unknown };
      if (typeof parsed.name !== "string" || parsed.name.length === 0) continue;
      const args = parsed.arguments ?? {};
      results.push({
        id: `text_${randomId()}`,
        type: "function",
        function: {
          name: parsed.name,
          arguments: typeof args === "string" ? args : JSON.stringify(args),
        },
      });
    } catch {
      // Unparseable — skip silently, model có thể emit malformed JSON
    }

    // Advance regex past this block để tránh re-match cùng "name"
    nameRe.lastIndex = endIdx;
  }

  return results;
}

function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/// Validate args có đủ required fields theo JSON Schema của tool.
/// Returns list field names thiếu (undefined / null / empty / whitespace-only string).
/// Số 0 và boolean false KHÔNG count là thiếu — chỉ chuỗi rỗng / whitespace.
function validateRequiredArgs(
  args: Record<string, unknown>,
  tool: Tool,
): string[] {
  const params = tool.parameters as { required?: unknown } | undefined;
  const required = params?.required;
  if (!Array.isArray(required)) return [];
  const missing: string[] = [];
  for (const field of required) {
    if (typeof field !== "string") continue;
    const value = args[field];
    if (value === undefined || value === null) {
      missing.push(field);
      continue;
    }
    // Trim string check — "" hoặc "   " (whitespace only) cũng coi là missing.
    if (typeof value === "string" && value.trim() === "") {
      missing.push(field);
    }
  }
  return missing;
}

/// Auto-resolve placeholder `*_id` args bằng cách look back trace history.
/// AI batch hay emit `group_id="<name>"` thay vì UUID. Nếu prior trace có
/// create_*({name: "<X>"}) → {id: "<uuid>", ...}, ta substitute `group_id`
/// với UUID đó.
///
/// Heuristic: match args.<key>=<value-not-uuid> với prior trace có
/// trace.ok + trace.data.name === <value> + trace.data.id (string UUID).
/// Trả args đã resolve. Không mutate input.
function resolvePlaceholderArgs(
  args: Record<string, unknown>,
  history: Array<{ call: ToolCall; ok: boolean; data?: unknown; error?: string }>,
): Record<string, unknown> {
  const resolved: Record<string, unknown> = { ...args };
  for (const [key, value] of Object.entries(resolved)) {
    const isIdKey = key === "id" || key.endsWith("_id");
    if (!isIdKey) continue;
    if (typeof value !== "string" || UUID_RE.test(value)) continue;

    // Look for prior successful trace where data.name === value, data.id is UUID
    for (let i = history.length - 1; i >= 0; i--) {
      const trace = history[i];
      if (!trace.ok || !trace.data || typeof trace.data !== "object") continue;
      const data = trace.data as Record<string, unknown>;
      if (data.name === value && typeof data.id === "string" && UUID_RE.test(data.id)) {
        resolved[key] = data.id;
        break;
      }
    }
  }
  return resolved;
}

/// Detect model degenerate output: trailing tokens lặp lại quá nhiều lần
/// (vd glm-4.7-flash đôi khi emit "assistant\nassistant\n..." vô hạn khi confused).
/// Pattern: trong N chunks gần nhất, nếu tất cả chunks (trim) đều giống nhau
/// và non-empty → degenerate. Threshold N = 30 chunks (~vài chục token).
class DegenerateDetector {
  private recent: string[] = [];
  private readonly threshold = 30;

  push(chunk: string): boolean {
    this.recent.push(chunk);
    if (this.recent.length > this.threshold) this.recent.shift();
    if (this.recent.length < this.threshold) return false;
    const norm = this.recent.map((c) => c.trim()).filter((c) => c.length > 0);
    if (norm.length < this.threshold * 0.8) return false; // mostly whitespace, not stuck
    const set = new Set(norm);
    return set.size === 1; // all chunks giống nhau → stuck
  }

  reset(): void {
    this.recent = [];
  }
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/// Detect placeholder value mà AI dùng khi chưa biết real ID. 2 strategies:
///   1. Regex match text placeholder ("ID của X", "<id>", "TODO", v.v.)
///   2. Structured: parse args JSON, check `*_id` keys phải là UUID. Nếu key
///      có hậu tố `_id` hoặc tên `id` mà value KHÔNG phải UUID → coi là
///      placeholder (vd AI truyền name `"SFO booking session"` thay vì UUID).
///
/// Returns matched placeholder string, hoặc null nếu không có.
export function detectPlaceholder(argsRaw: string): string | null {
  if (!argsRaw) return null;
  // Pattern Vietnamese ("ID của ...") + English placeholders thường gặp
  const re = /\b(ID c[ủu]a [^"',}]+|<[a-z_][^>]*>|TODO|FIXME|placeholder|to[\s_-]?be[\s_-]?determined|TBD|<id>)\b/i;
  const m = argsRaw.match(re);
  if (m) return m[0];

  // Structured check: parse JSON, look for `*_id` keys with non-UUID values.
  // AI thường emit name của entity thay vì real UUID (vd "SFO booking session"
  // thay vì "90a72f67-bfab-4c2f-87c2-85c72a560049").
  try {
    const args = JSON.parse(argsRaw) as Record<string, unknown>;
    if (args && typeof args === "object" && !Array.isArray(args)) {
      for (const [k, v] of Object.entries(args)) {
        if (typeof v !== "string" || v === "") continue;
        const isIdKey = k === "id" || k.endsWith("_id");
        if (isIdKey && !UUID_RE.test(v)) {
          return `${k}="${v}" (expected UUID, got non-UUID — likely entity name)`;
        }
      }
    }
  } catch {
    // Args không parse JSON được — skip structured check
  }
  return null;
}

export interface RunToolLoopOptions {
  client: AgentClient;
  tools: Tool[];
  messages: ChatMessage[];        // initial history; mutated trong loop
  config?: InferConfig;
  maxIterations?: number;          // default 5

  // Callbacks (tất cả optional)
  onDelta?: (chunk: string) => void;
  onAssistantMessage?: (message: ChatMessage) => void;
  onToolCallStart?: (call: ToolCall) => void;
  onToolCallEnd?: (call: ToolCall, result: { ok: true; data: unknown } | { ok: false; error: string }) => void;
  /// Show confirm UI; resolve true → execute, false → cancel.
  onApprovalRequired?: (call: ToolCall, tool: Tool) => Promise<boolean>;
  onError?: (err: { code: string; message: string }) => void;
  onDone?: (reason: FinishReason) => void;
  signal?: AbortSignal;

  /// Sau khi main loop kết thúc + có tool calls đã exec → sinh message tổng kết
  /// CƠ HỌC (deterministic) từ traceCalls. Không call AI — tránh hallucinate +
  /// degenerate output.
  ///
  /// `true` → header mặc định "Đã hoàn thành X/N bước".
  /// `string` → custom header (vd "Task complete").
  /// `false` / unset → skip (no summary).
  requestSummary?: boolean | string;

  /// Callback nhận summary text. Gọi 1 lần sau main loop. App append text như
  /// chat message riêng để UI hiển thị (vd bubble "✓ Done" cuối chat).
  onSummary?: (summary: string) => void;
}

export interface RunToolLoopResult {
  /// Full updated history (messages array đã append assistant + tool turns).
  messages: ChatMessage[];
  /// Lý do dừng vòng cuối: stop / length / error / max_iterations.
  finish_reason: FinishReason | "max_iterations";
  /// Trace tool calls đã exec (kể cả rejected). `data` chứa result của tool
  /// thành công — dùng để auto-resolve placeholder của tool sau (vd group_id).
  tool_calls: Array<{ call: ToolCall; ok: boolean; data?: unknown; error?: string }>;
  /// Summary text từ AI phase cuối, nếu `requestSummary` set + thành công.
  summary?: string;
}

export async function runToolLoop(opts: RunToolLoopOptions): Promise<RunToolLoopResult> {
  const messages = opts.messages.slice();          // mutate copy
  // Default 15 (đủ cho multi-step batch: tạo group → tạo nhiều item). User
  // override qua opts.maxIterations.
  const maxIters = opts.maxIterations ?? 15;
  const traceCalls: RunToolLoopResult["tool_calls"] = [];
  let finishReason: FinishReason | "max_iterations" = "max_iterations";

  // Convert local Tool → wire shape (drop execute).
  const wireTools = opts.tools.map(({ name, description, parameters }) => ({
    name,
    description,
    parameters,
  }));

  for (let iter = 0; iter < maxIters; iter++) {
    let assistantContent = "";
    let pendingCalls: ToolCall[] = [];
    let gotError = false;

    const degen = new DegenerateDetector();
    try {
      for await (const ev of opts.client.stream({
        messages,
        tools: wireTools.length > 0 ? wireTools : undefined,
        config: opts.config,
        signal: opts.signal,
      })) {
        if (ev.type === "delta") {
          assistantContent += ev.content;
          opts.onDelta?.(ev.content);
          if (degen.push(ev.content)) {
            // Model stuck repeating same token → abort
            gotError = true;
            opts.onError?.({
              code: "degenerate_output",
              message: "Model output degenerated (repeating tokens). Aborted stream.",
            });
            finishReason = "error";
            break;
          }
        } else if (ev.type === "tool_calls") {
          pendingCalls = ev.tool_calls;
        } else if (ev.type === "error") {
          gotError = true;
          opts.onError?.({ code: ev.code, message: ev.message });
          finishReason = "error";
        } else if (ev.type === "done") {
          finishReason = ev.finish_reason;
        }
      }
    } catch (e) {
      if (e instanceof GatewayError) {
        gotError = true;
        opts.onError?.({ code: e.code, message: e.message });
      } else if ((e as Error).name === "AbortError") {
        throw e;
      } else {
        opts.onError?.({ code: "client_error", message: (e as Error).message });
        gotError = true;
      }
      break;
    }

    // Fallback: nếu không có structured tool_calls events nhưng assistant content
    // chứa JSON tool calls (vd glm-4.7-flash) → parse text. Override finishReason
    // để loop tiếp tục execute thay vì kết thúc ở "stop".
    //
    // EXECUTE TẤT CẢ EXTRACTED CALLS: trước đây limit-to-first để tránh chained
    // dependencies bị batch (call sau dùng output call trước). Hiện không cần vì
    // `detectPlaceholder` đã check `*_id` keys phải là UUID — nếu AI emit batch
    // với entity name làm group_id (không phải UUID), tool sẽ fail fast với
    // placeholder error → AI retry với real ID ở iter sau. Cho phép batch execute
    // tránh trường hợp AI emit 4 calls, ta exec 1, AI tưởng done → summary
    // hallucinate cho 3 calls chưa exec.
    if (pendingCalls.length === 0 && assistantContent) {
      const extracted = extractTextToolCalls(assistantContent);
      if (extracted.length > 0) {
        pendingCalls = extracted;
        if (finishReason !== "error") finishReason = "tool_calls";
      }
    }

    // Append assistant message vào history (kể cả khi rỗng nếu chỉ có tool_calls)
    const asstMsg: ChatMessage = pendingCalls.length > 0
      ? { role: "assistant", content: assistantContent, tool_calls: pendingCalls }
      : { role: "assistant", content: assistantContent };
    messages.push(asstMsg);
    opts.onAssistantMessage?.(asstMsg);

    if (gotError || finishReason === "stop" || finishReason === "length") break;

    if (pendingCalls.length === 0) {
      // Defensive: nếu finish_reason là tool_calls nhưng không có call → break.
      break;
    }

    // Execute từng tool sequentially. Có thể song song hoá Phase 2.
    for (const call of pendingCalls) {
      const tool = opts.tools.find((t) => t.name === call.function.name);
      opts.onToolCallStart?.(call);

      let result: { ok: true; data: unknown } | { ok: false; error: string };

      // Parse args lên object.
      let args: Record<string, unknown> = {};
      try {
        args = call.function.arguments ? JSON.parse(call.function.arguments) : {};
      } catch {
        args = {};
      }

      // Auto-resolve placeholder: nếu args.<*_id> là non-UUID, tìm trong trace
      // history prior call create_* có data.name === value → substitute UUID.
      // Cho phép AI batch emit group_id="entity_name" mà vẫn work (group được
      // tạo ở trace trước trong cùng batch).
      args = resolvePlaceholderArgs(args, traceCalls);

      // Placeholder check trên args ĐÃ resolved. Nếu vẫn còn (không match trace
      // nào) → fail fast với error message → AI retry với real value.
      const placeholder = detectPlaceholder(JSON.stringify(args));

      // Validate required fields theo JSON Schema (chỉ check khi có tool match).
      const missingRequired = tool ? validateRequiredArgs(args, tool) : [];

      if (!tool) {
        result = { ok: false, error: `unknown tool: ${call.function.name}` };
      } else if (missingRequired.length > 0) {
        // Required field thiếu → AI phải gọi ask_user để hỏi value.
        result = {
          ok: false,
          error:
            `Missing required field(s): ${missingRequired.join(", ")}. ` +
            `STOP guessing. Call ask_user({question:"<câu hỏi>"}) ngay để hỏi user, ` +
            `rồi dùng user_answer làm value. KHÔNG retry tool này với value bịa.`,
        };
      } else if (placeholder) {
        // Args có *_id non-UUID (vd "grp-x", "<id>", entity name). 3 options cho AI.
        result = {
          ok: false,
          error:
            `Invalid placeholder: ${placeholder}. ` +
            `STOP. KHÔNG retry với value bịa khác. Choose ONE option: ` +
            `(A) If value là TÊN entity tồn tại → call find_*_by_name({name:"${placeholder.split("=")[1] ?? ""}"}) để lookup real UUID. ` +
            `(B) If thiếu info → call ask_user({question:"<hỏi user>"}). ` +
            `(C) If cần tạo mới entity → call create_* để có ID trước.`,
        };
      } else {
        let approved = true;
        if (tool.needsApproval && opts.onApprovalRequired) {
          try {
            approved = await opts.onApprovalRequired(call, tool);
          } catch {
            approved = false;
          }
        }
        if (!approved) {
          result = { ok: false, error: "rejected by user" };
        } else {
          try {
            const data = await tool.execute(args);
            result = { ok: true, data };
          } catch (e) {
            result = { ok: false, error: (e as Error).message };
          }
        }
      }

      traceCalls.push({
        call,
        ok: result.ok,
        ...(result.ok ? { data: result.data } : { error: result.error }),
      });
      opts.onToolCallEnd?.(call, result);

      // Append tool result message để model thấy.
      messages.push({
        role: "tool",
        tool_call_id: call.id,
        name: call.function.name,
        content: JSON.stringify(result.ok ? result.data : { error: result.error }),
      });
    }
    // continue loop → re-invoke /infer với tool results
  }

  // ─── Summary phase (deterministic) ──────────────────────────────
  // Khi requestSummary=true + có tool call nào đã exec → sinh message tổng kết
  // CƠ HỌC từ traceCalls (không call AI). Lý do bỏ AI summary:
  //   - glm-4.7-flash dễ degenerate trên long context (spam role token)
  //   - AI hay hallucinate ID khi tool fail → unreliable
  //   - Deterministic = fast + free + accurate (chỉ dùng data thực)
  //
  // Format: header với count + grouped tool name + listed failures với error.
  // requestSummary có thể là string để override header (vd "Task complete").
  let summary: string | undefined;
  if (opts.requestSummary && traceCalls.length > 0) {
    summary = buildDeterministicSummary(traceCalls, opts.requestSummary);
    const summaryMsg: ChatMessage = { role: "assistant", content: summary };
    messages.push(summaryMsg);
    opts.onSummary?.(summary);
  }

  opts.onDone?.(finishReason as FinishReason);
  return { messages, finish_reason: finishReason, tool_calls: traceCalls, summary };
}

/// Tạo summary text từ trace calls. Group ok/fail, count theo tool name.
function buildDeterministicSummary(
  traceCalls: RunToolLoopResult["tool_calls"],
  requestSummary: boolean | string,
): string {
  const ok = traceCalls.filter((t) => t.ok);
  const fail = traceCalls.filter((t) => !t.ok);
  const header =
    typeof requestSummary === "string"
      ? requestSummary
      : fail.length === 0
        ? `Đã hoàn thành ${ok.length}/${traceCalls.length} bước`
        : `Hoàn thành ${ok.length}/${traceCalls.length} bước (${fail.length} fail)`;

  const lines: string[] = [header];

  // Group ok bằng tool name, count.
  if (ok.length > 0) {
    const byName = new Map<string, number>();
    for (const t of ok) {
      const n = t.call.function.name;
      byName.set(n, (byName.get(n) ?? 0) + 1);
    }
    for (const [name, count] of byName) {
      lines.push(`✓ ${name}${count > 1 ? ` (×${count})` : ""}`);
    }
  }

  // List từng fail với error msg.
  if (fail.length > 0) {
    for (const t of fail) {
      const err = t.error ? `: ${t.error}` : "";
      lines.push(`✗ ${t.call.function.name}${err}`);
    }
  }

  return lines.join("\n");
}
